import { formatUrlSuffix, get, getApiUrl, insertURLQuery, SiteRouterKey } from '../utils';
import { QueryKey } from '../constants';

class BaseSiteRouterManager<SiteKey extends string> {
    /**
     * 取二级路径
     * @protected
     */
    protected get basePath() {
        return '';
    }

    /**
     * 取站点配置
     * @protected
     */
    protected get site() {
        return {};
    }

    /**
     * 取路由配置
     * @protected
     */
    protected get router() {
        return {};
    }

    /**
     * 获取相对路径（二级路径+路由）的方法
     * @param routerKey
     * @param args
     * @protected
     */
    protected relativePath(routerKey: SiteRouterKey, ...args: (string | number)[]) {
        const router = this.getRouter(routerKey, ...args);
        return formatUrlSuffix(this.basePath, router);
    }

    /**
     * 获取带参相对路径（二级路径+路由+参数）的方法
     * @param query
     * @param routerKey
     * @param args
     * @protected
     */
    protected queryRelativePath(
        query: { key: string; value: string | number }[],
        routerKey: SiteRouterKey,
        ...args: (string | number)[]
    ) {
        const relativePath = this.relativePath(routerKey, ...args);
        return insertURLQuery(relativePath, query);
    }

    /**
     * 获取站点绝对路径（站点+二级路径+路由）的方法
     * @param siteKey
     * @param routerKey
     * @param args
     * @protected
     */
    protected absolutePath(
        siteKey: SiteKey,
        routerKey: SiteRouterKey,
        ...args: (string | number)[]
    ) {
        const site = this.getSite(siteKey);
        const relativePath = this.relativePath(routerKey, ...args);
        return formatUrlSuffix(site, relativePath);
    }

    /**
     * 获取带参站点绝对路径（站点+二级路径+路由+参数）的方法
     * @param query
     * @param siteKey
     * @param routerKey
     * @param args
     * @protected
     */
    protected queryAbsolutePath(
        query: { key: QueryKey; value: string | number }[],
        siteKey: SiteKey,
        routerKey: SiteRouterKey,
        ...args: (string | number)[]
    ) {
        const absolutePath = this.absolutePath(siteKey, routerKey, ...args);
        return insertURLQuery(absolutePath, query);
    }

    /**
     * 获取站点拼接二级路径
     * @param siteKey
     * @protected
     */
    protected getSiteWithBasePath(siteKey: SiteKey) {
        const site = this.getSite(siteKey);
        return this.addBasePath(site);
    }

    /**
     * 向站点拼接二级路径
     * @param site
     * @protected
     */
    protected addBasePath(site: string) {
        if (!this.basePath) {
            return site;
        }
        return formatUrlSuffix(site, this.basePath);
    }

    /**
     * 获取路由
     * @param routerKey
     * @param args
     * @private
     */
    private getRouter(routerKey: SiteRouterKey, ...args: (string | number)[]) {
        return getApiUrl(get(this.router, routerKey, ''), ...args);
    }

    /**
     * 获取站点
     * @param siteKey
     * @private
     */
    private getSite(siteKey: SiteKey) {
        return get(this.site, siteKey, '');
    }
}

export default BaseSiteRouterManager;
